import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { classNames } from "helpers";
import { ModalProps, ZPosition } from "./types";
import { useDialog } from "ui/modules/public/components/DialogProvider/DialogProvider";

// Global counter for tracking open modals
let openModalCount = 0;

const Modal: React.FC<ModalProps> = (props: ModalProps) => {
  const {
    children,
    show,
    size,
    position = "side",
    side = "right",
    onHide,
    zPosition = "middle",
    intro,
    showOverlay = true
  } = props;

  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);

  const { createDialog } = useDialog();

  // Block scrolling when modal is open - using ref counting for multiple modals
  useEffect(() => {
    if (show) {
      setHasBeenOpened(true);
      openModalCount++;
      document.body.classList.add("overflow-hidden");

      // Trigger animation after mount so CSS transition kicks in
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimateIn(true));
      });

      return () => {
        openModalCount--;
        if (openModalCount === 0) {
          document.body.classList.remove("overflow-hidden");
        }
        setAnimateIn(false);
      };
    }
  }, [show]);

  const handleHideClick = () => {
    if (onHide) {
      onHide();
      openDialog();
    }
  }

  const openDialog = () => {
    createDialog({
      onConfirm: handleHideClick
    })
  }

  /**
   * Širine idu od najmanjeg ekrana prema većem. Bez širine za male ekrane modal se ravna prema
   * sadržaju, pa na užem prozoru ispadne ili preuzak ili širi od ekrana — zato svaka veličina
   * kreće od `w-full`.
   */
  const sizeClassName = () => {
    switch (size) {
      case "small":
        return "w-full sm:w-4/5 md:w-1/2 lg:w-1/3 xl:w-1/4";
      case "medium":
        return "w-full md:w-5/6 lg:w-2/3 xl:w-3/6";
      case "large":
        return "w-full lg:w-5/6 xl:w-4/6";
      case "full":
        return "w-full";
      default:
        return "w-full md:w-2/3 lg:w-1/2 xl:w-1/3";
    }
  };

  const zPositionClassName = (zPosition: ZPosition | "middle") => {
    switch (zPosition) {
      case ZPosition.Bottom:
        return "z-30";
      case ZPosition.Middle:
        return "z-40";
      case ZPosition.Top:
        return "z-50";
      default:
        return "";
    }
  };

  const sidePositionClass = () => {
    switch (side) {
      case "left":
        return "justify-start items-center";
      case "right":
        return "justify-end items-center";
      case "top":
        return "justify-center items-start";
      case "bottom":
        return "justify-center items-end";
      default:
        return "justify-end items-center";
    }
  };

  const sideTranslateClass = () => {
    if (animateIn) return "translate-x-0 translate-y-0";

    switch (side) {
      case "left":
        return "-translate-x-full";
      case "right":
        return "translate-x-full";
      case "top":
        return "-translate-y-full";
      case "bottom":
        return "translate-y-full";
      default:
        return "translate-x-full";
    }
  };

  // Shared content layout: flex column with header, body, footer
  const renderContentLayout = () => (
    <div className="flex flex-col h-full min-h-0">
      {children}
    </div>
  );

  const renderIntroLayout = () => (
    <div className="w-full h-full flex">
      {/* Intro panel */}
      <div
        className={classNames(
          "transition-all duration-500 ease-in-out h-full overflow-hidden",
          animateIn ? "w-1/2" : "w-0"
        )}
      >
        {intro?.content}
      </div>

      {/* Content panel */}
      <div
        className={classNames(
          "bg-white h-full transition-all duration-500 ease-in-out overflow-hidden",
          animateIn ? "w-1/2" : "w-full"
        )}
      >
        {renderContentLayout()}
      </div>
    </div>
  );

  const renderModalContent = () => {
    if (position === "side") {
      return (
        <div
          className={classNames(
            sizeClassName(),
            "h-full bg-white shadow-lg transform transition-all duration-500 ease-in-out",
            sideTranslateClass()
          )}
        >
          {renderContentLayout()}
        </div>
      );
    }
    // Centered modal — visina je ograničena na ekran, inače na niskom prozoru sadržaj ispadne
    // izvan vidljivog dijela, a podnožje s gumbima završi ispod ruba. `h-full` se ne računa prema
    // `max-h`, pa kontejner mora biti flex da tijelo modala skrola unutar tih 90vh.
    // `full` zauzima cijeli ekran — adresni modali kartu i podnožje slažu prema punoj visini.
    return (
      <div
        className={classNames(
          sizeClassName(),
          size === "full" ? "h-full" : "max-h-[90vh] rounded-md",
          "flex flex-col bg-white shadow-lg transform transition-all duration-500",
          animateIn ? "scale-100 opacity-100" : "scale-95 opacity-0"
        )}
      >
        {renderContentLayout()}
      </div>
    );
  };

  if (!show) return null;

  // Portal target: render inside body
  return ReactDOM.createPortal(
    <>
      <div
        className={classNames(
          "fixed inset-0 flex transition-opacity duration-500",
          intro
            ? zPositionClassName(ZPosition.Bottom)
            : position === "side"
              ? `${sidePositionClass()} ${zPositionClassName(ZPosition.Bottom)}`
              : `justify-center items-center  ${zPositionClassName(zPosition)}`,
          animateIn ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
      >

        {/* Siva podloga: nema je kad se prikazuje intro. Čarobnjaci koji intro pale po
            koraku je gase i za ostale korake (`showOverlay`) — inače podloga na koraku
            bez introa bljesne sivo pa se vrati. */}
        {!intro && showOverlay && (
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity duration-500"
            onClick={() => openDialog()}
          />
        )}

        {/* Modal content */}
        {intro ? renderIntroLayout() : renderModalContent()}
      </div>
    </>,
    document.body
  );

};

export default Modal;
